import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { useUserStore } from './userStore';

export interface Message {
  id: string;
  content: string;
  senderId: string;
  timestamp: string;
  isRead: boolean;
}

export interface Conversation {
  id: string;
  participants: string[];
  recipientId: string;
  recipientName: string;
  listingId: string;
  listingTitle: string;
  messages: Message[];
  unreadCount: number;
}

interface MessageStore {
  conversations: Conversation[];
  createConversation: (data: { 
    participants: string[];
    recipientId: string;
    recipientName: string;
    listingId: string;
    listingTitle: string 
  }) => string;
  addMessage: (conversationId: string, message: Omit<Message, 'id' | 'timestamp'>) => void;
  markConversationAsRead: (conversationId: string, userId: string) => void;
  getConversationsByUser: (userId: string) => Conversation[];
  getConversationById: (conversationId: string) => Conversation | undefined;
  getUnreadCount: (userId: string) => number;
  resetStore: () => void;
}

const initialState = {
  conversations: []
};

export const useMessageStore = create<MessageStore>()(
  persist(
    (set, get) => ({
      ...initialState,

      createConversation: (data) => {
        const { conversations } = get();
        
        // Check for existing conversation
        const existingConv = conversations.find(conv => 
          conv.listingId === data.listingId && 
          conv.participants.includes(data.participants[0]) &&
          conv.participants.includes(data.participants[1])
        );

        if (existingConv) {
          return existingConv.id;
        }

        const id = Date.now().toString();
        const newConversation: Conversation = {
          id,
          participants: data.participants,
          recipientId: data.recipientId,
          recipientName: data.recipientName,
          listingId: data.listingId,
          listingTitle: data.listingTitle,
          messages: [],
          unreadCount: 0
        };

        set(state => ({
          conversations: [...state.conversations, newConversation]
        }));

        return id;
      },

      addMessage: (conversationId, messageData) => {
        const newMessage: Message = {
          id: Date.now().toString(),
          ...messageData,
          timestamp: new Date().toISOString(),
          isRead: false
        };

        set(state => ({
          conversations: state.conversations.map(conv => {
            if (conv.id === conversationId) {
              const unreadCount = messageData.senderId !== conv.recipientId ? 
                conv.unreadCount + 1 : 
                conv.unreadCount;
              
              return {
                ...conv,
                messages: [...conv.messages, newMessage],
                unreadCount
              };
            }
            return conv;
          })
        }));
      },

      markConversationAsRead: (conversationId, userId) => {
        set(state => ({
          conversations: state.conversations.map(conv => {
            if (conv.id === conversationId && conv.recipientId === userId) {
              return {
                ...conv,
                messages: conv.messages.map(msg => ({
                  ...msg,
                  isRead: true
                })),
                unreadCount: 0
              };
            }
            return conv;
          })
        }));
      },

      getConversationsByUser: (userId) => {
        const { conversations } = get();
        return conversations.filter(conv => 
          conv.participants.includes(userId)
        ).map(conv => {
          // Get the other participant's name from the user store
          const otherParticipantId = conv.participants.find(id => id !== userId);
          const users = useUserStore.getState().users;
          const otherUser = otherParticipantId ? users[otherParticipantId] : null;
          
          return {
            ...conv,
            recipientName: otherUser?.fullName?.split(' ')[0] || 'Unknown User'
          };
        });
      },

      getConversationById: (conversationId) => {
        const { conversations } = get();
        return conversations.find(conv => conv.id === conversationId);
      },

      getUnreadCount: (userId) => {
        const { conversations } = get();
        return conversations.reduce((total, conv) => {
          if (conv.recipientId === userId) {
            return total + conv.unreadCount;
          }
          return total;
        }, 0);
      },

      resetStore: () => set(initialState)
    }),
    {
      name: 'message-store',
      version: 1
    }
  )
);